import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, pipe } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { EventService } from 'src/app/services/event.service';
import { AppState } from 'src/app/store/app.reducer';
import * as EventActions from './event.actions';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { TranslateService } from '@ngx-translate/core';
import * as NotificationsActions from '../notifications/notifications.actions';
import { PaginationParams } from 'src/app/model/api/pagination-params';

@Injectable()
export class EventEffects {
    getEvents$ = createEffect(() => this.actions$.pipe(
        ofType(EventActions.getEvents),
        withLatestFrom(
            this.store.select('event', 'pagination'),
            this.store.select('event', 'filter'),
            this.store.select('user', 'currentTechnician'),
            this.store.select('user', 'isImpersMode'),
            this.store.select('user', 'currentLang'),
        ),
        switchMap(([action, pagination, filter, currentTechnician, isImpersMode, currentLang]) => {
            let userId = action.userId;
            if (isImpersMode) {
                userId = currentTechnician.PersonifiedId;
            }
            let lang = action.language;
            if (!lang) {
                lang = currentLang.code;
            }
            return this.eventService.getEvents(
                lang, pagination, filter, userId
            ).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return EventActions.getEventsSuccess({ paging: resp.Data.Paging, events: resp.Data.Items });
                    } else {
                        return EventActions.getEventsFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(EventActions.getEventsFailed({ errorMessage: error.message }));
                })
            );
        })
    ));


    getEventsAll$ = createEffect(() => this.actions$.pipe(
        ofType(EventActions.getEventsAll),
        withLatestFrom(
            this.store.select('event', 'filter'),
            this.store.select('user', 'currentTechnician'),
            this.store.select('user', 'isImpersMode'),
            this.store.select('user', 'currentLang'),
        ),
        switchMap(([action, filter, currentTechnician, isImpersMode, currentLang]) => {
            let userId = action.userId;
            if (isImpersMode) {
                userId = currentTechnician.PersonifiedId;
            }
            let lang = action.language;
            if (!lang) {
                lang = currentLang.code;
            }
            const paging: PaginationParams = {
                size: 10000,
                page: 1,
                sort: 'EventDate',
                order: 'desc',
            };
            return this.eventService.getEvents(
                lang, paging, filter, userId
            ).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return EventActions.getEventsAllSuccess({ paging: resp.Data.Paging, events: resp.Data.Items });
                    } else {
                        return EventActions.getEventsAllFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(EventActions.getEventsAllFailed({ errorMessage: error.message }));
                })
            );
        })
    ));

    getEventTypes$ = createEffect(() => this.actions$.pipe(
        ofType(EventActions.getEventTypes),
        switchMap(() => this.eventService.getEventTypes().pipe(
            map((resp) => {
                if (resp.ErrorMessage) {
                    return EventActions.getEventTypesFailed({ errorMessage: 'Nasty Error' });
                }
                if (!resp.Data) {
                    return EventActions.getEventTypesFailed({ errorMessage: 'Nasty Error' });
                }
                return EventActions.getEventTypesSuccess({ eventTypes: resp.Data });
            }),
            catchError(error => {
                console.error(error);
                return of(EventActions.getEventTypesFailed({ errorMessage: error.message }));
            })
        ))
    ));

    getEmailConfigs$ = createEffect(() => this.actions$.pipe(
        ofType(
            EventActions.getEmailConfigs,
            EventActions.saveEmailConfigsSuccess,
            EventActions.deleteEmailConfigsSuccess
        ),
        switchMap((action) => this.eventService.getEmailDeviceConfig()
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return EventActions.getEmailConfigsSuccess({ emailConfigs: resp.Data });
                    } else {
                        return EventActions.getEmailConfigsFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(EventActions.getEmailConfigsFailed({ errorMessage: error.message })))
            )
        )
    ));

    saveEmailConfig$ = createEffect(() => this.actions$.pipe(
        ofType(EventActions.saveEmailConfigs),
        pipe(withLatestFrom(this.store.select('device', 'currentDevice'))),
        switchMap(([action, device]) => {
            const deviceId = action.deviceId || device.Id;
            return this.eventService.saveEmailDeviceConfig(deviceId, action.level).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return EventActions.saveEmailConfigsSuccess({ message: this.translate.instant('UPDATE_NOTIFICATION_SETTINGS_SUCCESS') });
                    } else {
                        return EventActions.saveEmailConfigsFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(EventActions.saveEmailConfigsFailed({ errorMessage: error.message })))
            );
        })
    ));

    deleteEmailConfig$ = createEffect(() => this.actions$.pipe(
        ofType(EventActions.deleteEmailConfigs),
        map(action => action.configId),
        switchMap((configId) => this.eventService.deleteEmailConfig(configId)
            .pipe(
                map((resp) => EventActions.deleteEmailConfigsSuccess({ message: this.translate.instant('UPDATE_NOTIFICATION_SETTINGS_SUCCESS') })),
                catchError(error => of(EventActions.deleteEmailConfigsFailed({ errorMessage: error.message })))
            )
        )
    ));

    getManeuversConfigs$ = createEffect(() => this.actions$.pipe(
        ofType(EventActions.getManeuversNotifConfigs),
        switchMap((action) => this.eventService.getManeuversConfig(action.deviceId)
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return EventActions.getManeuversNotifConfigsSuccess({ maneuverConfigs: resp.Data });
                    } else {
                        return EventActions.getManeuversNotifConfigsFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(EventActions.getManeuversNotifConfigsFailed({ errorMessage: error.message })))
            )
        )
    ));

    createManeuversConfig$ = createEffect(() => this.actions$.pipe(
        ofType(EventActions.createManeuversNotifConfig),
        switchMap((action) => this.eventService.createManeuversConfig(action.config)
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return EventActions.createManeuversNotifConfigSuccess({
                            message: this.translate.instant('UPDATE_NOTIFICATION_SETTINGS_SUCCESS'),
                            config: { ...action.config, Id: resp.Data.Id }
                        });
                    } else {
                        return EventActions.createManeuversNotifConfigFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(EventActions.createManeuversNotifConfigFailed({ errorMessage: error.message })))
            )
        )
    ));

    updateManeuversConfig$ = createEffect(() => this.actions$.pipe(
        ofType(EventActions.updateManeuversNotifConfig),
        switchMap((action) => this.eventService.updateManeuversConfig(action.config)
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return EventActions.updateManeuversNotifConfigSuccess({
                            config: action.config,
                            message: this.translate.instant('UPDATE_NOTIFICATION_SETTINGS_SUCCESS'),
                        });
                    } else {
                        return EventActions.updateManeuversNotifConfigFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(EventActions.updateManeuversNotifConfigFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteManeuversConfig$ = createEffect(() => this.actions$.pipe(
        ofType(EventActions.deleteManeuversNotifConfig),
        switchMap((action) => this.eventService.deleteManeuversConfig(action.configId)
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return EventActions.deleteManeuversNotifConfigSuccess({
                            message: this.translate.instant('UPDATE_NOTIFICATION_SETTINGS_SUCCESS')
                        });
                    } else {
                        return EventActions.deleteManeuversNotifConfigFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(EventActions.deleteManeuversNotifConfigFailed({ errorMessage: error.message })))
            )
        )
    ));


    //NOTIFY ADVANCE DEVICE
    getNotifyAdvanceDevice$ = createEffect(() => this.actions$.pipe(
        ofType(
            EventActions.getNotifyAdvanceDevice, EventActions.editNotifyAdvanceDeviceSuccess, EventActions.editNotifyAdvanceDeviceFailed
        ),
        switchMap((action) => this.eventService.getNotifyAdvanceDevice(action.deviceId, action.ownerId)
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return EventActions.getNotifyAdvanceDeviceSuccess({ listNotifyConfig: resp.Data });
                    } else {
                        return EventActions.getNotifyAdvanceDeviceFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(EventActions.getEmailConfigsFailed({ errorMessage: error.message })))
            )
        )
    ));



    editNotifyAdvanceDevice$ = createEffect(() => this.actions$.pipe(
        ofType(EventActions.editNotifyAdvanceDevice),
        switchMap((action) => this.eventService.editNotifyAdvanceDevice(action.deviceId, action.notify, action.ownerId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return EventActions.editNotifyAdvanceDeviceSuccess({ message: this.translate.instant('EDIT_NOTIFY_ADVANGE_SUCCESS'), deviceId: action.deviceId, ownerId: action.ownerId });
                    } else {
                        return EventActions.editNotifyAdvanceDeviceFailed({ errorMessage: this.translate.instant('EDIT_NOTIFY_ADVANGE_ERROR'), deviceId: action.deviceId, ownerId: action.ownerId });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        return of(EventActions.editNotifyAdvanceDeviceFailed({ errorMessage: this.translate.instant('EDIT_NOTIFY_ADVANGE_ERROR'), deviceId: action.deviceId, ownerId: action.ownerId }));
                    } else {
                        return of(EventActions.editNotifyAdvanceDeviceFailed({ errorMessage: error.message, deviceId: action.deviceId, ownerId: action.ownerId }));
                    }
                })
            )
        )
    ));

    //NOTIFITY ADVANCE DEVICE

    deleteEmailEventsNotifications$ = createEffect(() => this.actions$.pipe(
        ofType(EventActions.deleteEmailEventsNotifications),
        switchMap((action) => this.eventService.deleteEmailEventsNotifications(action.token)
            .pipe(
                map(() => EventActions.deleteEmailEventsNotificationsSuccess({ message: this.translate.instant('UPDATE_NOTIFICATION_SETTINGS_SUCCESS') })),
                catchError(error => of(EventActions.deleteEmailEventsNotificationsFailed({ errorMessage: error.message })))
            )
        )
    ));

    notifySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(
            EventActions.deleteManeuversNotifConfigSuccess,
            EventActions.updateManeuversNotifConfigSuccess,
            EventActions.createManeuversNotifConfigSuccess,
            EventActions.saveEmailConfigsSuccess,
            EventActions.deleteEmailConfigsSuccess,
            EventActions.editNotifyAdvanceDeviceSuccess,
        ),
        tap((action) => {
            if (action.message === '') {
                return;
            }
            const notification = new Notification("EVENT", action.message, NotificationLevel.Success, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    notifyFailure$ = createEffect(() => this.actions$.pipe(
        ofType(
            EventActions.deleteManeuversNotifConfigFailed,
            EventActions.updateManeuversNotifConfigFailed,
            EventActions.createManeuversNotifConfigFailed,
            EventActions.getNotifyAdvanceDeviceFailed,
            EventActions.editNotifyAdvanceDeviceFailed,
            EventActions.getEmailConfigsFailed,
            EventActions.saveEmailConfigsFailed,
            EventActions.deleteEmailConfigsFailed,
            EventActions.editNotifyAdvanceDeviceFailed,
            EventActions.getEventsFailed,

        ),
        tap((action) => {
            const notification = new Notification("EVENT", action.errorMessage, NotificationLevel.Error, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    constructor(
        private actions$: Actions,
        private store: Store<AppState>,
        private eventService: EventService,
        private translate: TranslateService,
    ) { }
}
